import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WorkerMultiSelect } from "@/components/WorkerMultiSelect";
import { Megaphone, Pencil, Trash2, Plus, Loader2, Eye, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const EMPTY = { title: "", message: "", recipientIds: [] };
const LANG_NAMES = { pl: "Polish", uk: "Ukrainian", ro: "Romanian", hu: "Hungarian" };

const fmtSeen = (ts) => new Date(ts).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const SeenReceipts = ({ n }) => {
  const recips = n.recipients || (n.recipientIds || []).map((id) => ({ id, name: "—" }));
  const seen = new Map((n.seenBy || []).map((s) => [s.userId, s]));
  const count = recips.filter((r) => seen.has(r.id)).length;
  if (!recips.length) return null;
  const all = count === recips.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${all ? "text-emerald-600" : "text-slate-500"} hover:underline`} data-testid={`day-notice-seen-${n.id}`}>
          {all ? <CheckCheck className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}Seen {count}/{recips.length}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-1.5 text-sm" data-testid={`day-notice-seen-list-${n.id}`}>
        {recips.map((r) => {
          const s = seen.get(r.id);
          return (
            <div key={r.id} className="flex items-center justify-between gap-2">
              <span className="truncate text-slate-800">{s?.name || r.name}</span>
              {s ? <span className="text-[11px] text-emerald-600 shrink-0">Seen {fmtSeen(s.seenAt)}</span> : <span className="text-[11px] text-slate-400 shrink-0">Not seen yet</span>}
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};

// Day notices for one site + date, shown inside the shift detail dialog.
export const DayNotices = ({ event }) => {
  const { user } = useAuth();
  const canManage = ["owner", "admin", "office"].includes(user?.role);
  const [notices, setNotices] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | notice id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const cleaners = (event.workers?.length ? event.workers : event.workerId ? [{ id: event.workerId, name: event.workerName }] : [])
    .map((w) => ({ id: w.id, displayName: w.name }));

  const load = () => client.get(`/day-notices?from=${event.date}&to=${event.date}&siteId=${event.siteId}`)
    .then((r) => setNotices(r.data?.notices || [])).catch(() => setNotices([]));
  useEffect(() => { load(); }, [event.siteId, event.date]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setForm({ ...EMPTY, recipientIds: cleaners.map((c) => c.id) }); setEditing("new"); };
  const startEdit = (n) => { setForm({ title: n.title, message: n.message, recipientIds: n.recipientIds || [] }); setEditing(n.id); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await client.post("/day-notices", { date: event.date, siteId: event.siteId, ...form });
      else await client.patch(`/day-notices/${editing}`, form);
      toast.success(editing === "new" ? "Day notice added" : "Day notice updated");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete the notice "${n.title}"?`)) return;
    try { await client.delete(`/day-notices/${n.id}`); toast.success("Day notice deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (event.kind !== "shift" || (!canManage && !notices.length)) return null;
  const nameOf = (id) => cleaners.find((c) => c.id === id)?.displayName || "Cleaner";

  return (
    <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50/50 p-3" data-testid="day-notices">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" />Day notices</p>
        {canManage && editing === null && (
          <Button size="sm" variant="outline" className="h-7 bg-white" onClick={startNew} data-testid="day-notice-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add day notice</Button>
        )}
      </div>
      {notices.map((n) => editing === n.id ? null : (
        <div key={n.id} className="rounded-md bg-white border border-sky-100 px-3 py-2" data-testid={`day-notice-${n.id}`}>
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-sm text-slate-900">{n.title}</p>
            {canManage && (
              <div className="flex gap-1 shrink-0">
                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => startEdit(n)} data-testid={`day-notice-edit-${n.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => remove(n)} data-testid={`day-notice-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
          {n.message && <p className="text-sm text-slate-600 whitespace-pre-wrap mt-0.5">{n.message}</p>}
          <p className="text-[11px] text-slate-400 mt-1">To: {(n.recipients || []).map((r) => r.name).join(", ") || (n.recipientIds || []).map(nameOf).join(", ") || "nobody"} · by {n.createdByName}</p>
          {canManage && <SeenReceipts n={n} />}
          {Object.keys(n.translations || {}).length > 0 && (
            <p className="text-[11px] text-sky-700 mt-1" data-testid={`day-notice-translated-${n.id}`}>
              Translated into: {Object.keys(n.translations).map((c) => LANG_NAMES[c] || c).join(", ")}
            </p>
          )}
        </div>
      ))}
      {editing !== null && (
        <div className="space-y-2 rounded-md bg-white border border-sky-200 p-3" data-testid="day-notice-form">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" maxLength={120} data-testid="day-notice-title" /></div>
          <div><Label>Details</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" rows={3} maxLength={2000} data-testid="day-notice-message" /></div>
          <div><Label>Send to</Label><WorkerMultiSelect workers={cleaners} value={form.recipientIds} onChange={(v) => setForm({ ...form, recipientIds: v })} testid="day-notice-recipients" /></div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="day-notice-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !form.title.trim()} data-testid="day-notice-save">{saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Save</Button>
          </div>
        </div>
      )}
      {!notices.length && editing === null && <p className="text-xs text-slate-400">No notices for this day.</p>}
    </div>
  );
};
